import { type ReactNode } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconChevronDown,
  IconDots,
  IconFileText,
  IconInfoCircle,
  IconLink,
  IconPin,
  IconPinnedOff,
  IconPlus,
  IconUserMinus,
  IconWorld,
} from "@tabler/icons-react";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopConfirmDialog } from "../../../components/app/DesktopConfirmDialog";
import { useConfirmation } from "../../../hooks/useConfirmation";
import { openDesktopRoomInfo } from "../../../components/room/DesktopRoomInfoHost";
import { RoomSupportedByChip } from "../../../components/room/RoomSupportedByChip";
import {
  displayToast,
  displayToastWithActions,
} from "../../../components/app/AppToast";
import { Avatar } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { usePopoverMenu } from "../../../components/ui/Popover";
import {
  DesktopMenu,
  DesktopMenuDivider,
  DesktopMenuItem,
  DesktopMenuRadioItem,
} from "../../../components/ui/DesktopMenu";
import {
  LANGUAGE_LABELS,
  LANGUAGE_ORDER,
} from "../../../components/settings/sheet/views/LanguageView";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import { cn } from "../../../lib/utils";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import {
  roomContentLangStore,
  setRoomContentLang,
} from "../../../domains/app/stores/roomContentLangStore";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import { useJoinRoom } from "../../../domains/room/hooks/useJoinRoom";
import { useCanShowJoin } from "../../../domains/room/hooks/useCanShowJoin";
import { useIsRoomMember } from "../../../domains/room/hooks/useIsRoomMember";
import { useShareRoom } from "../../../domains/room/hooks/useShareRoom";
import { usePinRoom } from "../../../domains/room/hooks/usePinRoom";
import {
  getRoomDescription,
  getRoomName,
} from "../../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../../domains/room/utils/getRoomLogoUrl";
import { reloadCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import { RoomOAuthGate } from "../../../components/room/RoomOAuthGate";
import { RoomCongressHero } from "../../../components/room/RoomCongressHero";
import { partnerAccentColor } from "../../../domains/congress/partnerAccentColor";
import { useCollectionScroll } from "../../playlist/desktop/useCollectionScroll";
import { contactLineKind } from "../../../domains/partners/roomWelcome";
import { gaEventRoomInfoOpened } from "../../../tools/analytics/domains/partners.analytics";
import { roomContentCounts } from "../../../domains/room/utils/roomContentCounts";
import { navClickChangeLanguage } from "../../../tools/analytics/domains/nav.analytics";
import type { SupportedLanguage } from "../../../interfaces";

/** The tabs a room can have, in the order they show. */
export type DesktopRoomTab = "discovery" | "resources";

const TAB_PATHS = {
  discovery: "/room/discovery",
  resources: "/room/resources",
} as const satisfies Record<DesktopRoomTab, string>;

/**
 * A room on a desktop (Figma "10 · Rooms"): Rooms is a destination of its own
 * in the sidebar, so the breadcrumb runs Rooms / the room. A white header (no atmospheric
 * visual) holds what the room is and who supports it, Follow, Share and the
 * room's menu; the tabs follow when the room has more than one; then the tab's
 * list beside its rail.
 */
export function DesktopRoomShell({
  room,
  tab,
  rail,
  children,
}: {
  room: PrivateRoom;
  tab: DesktopRoomTab;
  rail: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { roomTextLang } = useActiveLang();
  const roomName = getRoomName(room, roomTextLang);
  const { scrollRef, onScroll, flush } = useCollectionScroll();
  const tabs = (
    [
      "discovery",
      ...(room.resourcesTab === true ? (["resources"] as const) : []),
    ] as const
  ).map((value) => ({
    value,
    label: value === "discovery" ? t("Discovery") : t("Resources"),
  }));

  return (
    // Back from an article lands where the reader left the list.
    <div
      ref={scrollRef}
      onScroll={onScroll}
      onPointerDownCapture={flush}
      onKeyDownCapture={flush}
      className="flex h-full flex-col overflow-y-auto"
    >
      <RoomOAuthGate roomId={room.id} />
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{
              kind: "separate",
              onBack: () => {
                goBackInApp(history, "/rooms");
              },
            }}
            trail={[{ label: t("Rooms"), to: "/rooms" }]}
            current={roomName}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="px-10 pb-24">
        <div className="mx-auto flex max-w-desktop-block flex-col gap-10 pt-10">
          <RoomHeader room={room} roomName={roomName} />
          {/* Figma "D3.explore.room.congress-banner": a promoted congress
              space sits between the header and the tabs, on both tabs. */}
          <RoomCongressHero
            roomId={room.id}
            organisationName={room.organisationModel.name}
            accentColor={partnerAccentColor(room)}
            frame="flush"
          />
          <div className="flex flex-col gap-6">
            {tabs.length < 2 ? null : (
              <nav
                aria-label={t("Room sections")}
                className="flex gap-2 border-b border-ds-slate-100"
              >
                {tabs.map((item) => (
                  <Link
                    key={item.value}
                    to={`${TAB_PATHS[item.value]}/${room.id}`}
                    replace
                    aria-current={item.value === tab ? "page" : undefined}
                    className={cn(
                      "flex h-11.5 w-49 items-center justify-center border-b-3 text-footnote font-semibold no-underline transition-colors",
                      item.value === tab
                        ? "border-ds-mint-900 text-ds-mint-900"
                        : "border-transparent bg-ds-black/6 text-ds-slate-400 hover:text-ds-slate-800"
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            )}
            <div className="flex items-start gap-8">
              <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col">
                {children}
              </div>
              <aside className="sticky top-20 hidden w-desktop-rail shrink-0 flex-col gap-6 lg:flex">
                {rail}
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoomHeader({
  room,
  roomName,
}: {
  room: PrivateRoom;
  roomName: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { roomTextLang, contentLang } = useActiveLang();
  const description = getRoomDescription(room, roomTextLang);
  const logoUrl = getRoomLogoUrl(room);
  const isMember = useIsRoomMember(room.id);
  // The feed and the collection rails serve the content language only.
  const counts = roomContentCounts(room, contentLang);

  return (
    <header className="flex items-start justify-between gap-8">
      <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col gap-3">
        <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
          {roomName}
        </h1>
        {/* Figma D3 (4958:105821): the attribution sits under the name, the
            "Supported by" chip of the phone, for every room as on the phone
            (a Juisci room reads "Supported by Juisci"). The chip and the ⓘ
            are one target: the attribution is the question, and the room
            info dialog is the answer. */}
        {/* No aria-label: the visible chip text stays in the accessible name
            (WCAG 2.5.3), and the hidden words say what the button opens. */}
        <button
          type="button"
          onClick={() => {
            gaEventRoomInfoOpened({
              roomId: room.id,
              isMember,
              contactLine: contactLineKind(room.partnerContact, isMember),
            });
            openDesktopRoomInfo(room.id);
          }}
          className="group flex cursor-pointer items-center gap-1 self-start border-none bg-transparent p-0 text-left"
        >
          <RoomSupportedByChip
            organisationName={room.organisationModel.name}
            size="md"
          />
          <span className="flex size-6 shrink-0 items-center justify-center rounded-md text-ds-slate-400 group-hover:bg-ds-slate-100">
            <IconInfoCircle className="size-4" stroke={1.25} />
          </span>
          <span className="sr-only">{t("About this room")}</span>
        </button>
        {description === null ? null : (
          <p className="m-0 text-footnote text-ds-slate-500">{description}</p>
        )}
        {counts === null ? null : (
          <p className="m-0 flex items-center gap-1.5 text-footnote text-ds-slate-600">
            <IconFileText size={16} stroke={1.25} className="shrink-0" />
            {[
              t("{{count}} articles", { count: counts.articles }),
              t("{{count}} collections", { count: counts.collections }),
            ].join(" · ")}
          </p>
        )}
        <RoomActions room={room} roomName={roomName} />
      </div>
      <div className="flex size-22 shrink-0 items-center justify-center rounded-xl border border-ds-slate-100 bg-ds-white">
        {logoUrl === "" ? (
          <Avatar
            variant="initials"
            initials={roomName.charAt(0)}
            size="lg"
            badge={null}
          />
        ) : (
          <Avatar
            variant="image"
            src={logoUrl}
            alt=""
            imageFit="scale-down"
            size="lg"
            badge={null}
          />
        )}
      </div>
    </header>
  );
}

/**
 * Follow (before the join only), Share and the room's menu. One verb: Follow
 * is the join (`POST /join`), and its welcome opens once the membership is
 * saved. A member unfollows from the menu only, and the dialog asks first
 * (Figma "D3.explore.room.unfollow"); the toast still offers Undo on a public
 * room. A member pins the room from the menu too (Figma
 * "D3.explore.room.menu.pin"). The language is the one button beside Share
 * that is not an action on the room: it stays in sight because it changes what
 * the reader reads.
 */
function RoomActions({
  room,
  roomName,
}: {
  room: PrivateRoom;
  roomName: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { roomTextLang } = useActiveLang();
  const revalidate = useApiRevalidate();
  const isMember = useIsRoomMember(room.id);
  const { pinnedRoomId, pin, unpin } = usePinRoom();
  const isPinned = pinnedRoomId === room.id;
  // The room Pin to top replaces, named in the row: one pinned room at most.
  // The same query and input as `useIsRoomMember`, so no second request.
  const joinedRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    isMember ? { durable: true } : false
  );
  const otherPinnedRoom =
    joinedRoomsQuery.state === "success" && !isPinned
      ? joinedRoomsQuery.body.rooms.find((r) => r.id === pinnedRoomId)
      : null;
  const replacedRoomName =
    otherPinnedRoom === undefined || otherPinnedRoom === null
      ? null
      : getRoomName(otherPinnedRoom, roomTextLang);
  const canShowJoin = useCanShowJoin(room.id, room);
  const { joinRoom, isJoining } = useJoinRoom(room.id, "public app", "greet");
  const { joinRoom: rejoin } = useJoinRoom(room.id, "public app", "silent");
  const { triggerShare, copyLink, renderShareSheet } = useShareRoom(
    room.id,
    room
  );
  const menu = usePopoverMenu("right");
  // Follow takes the reader back into a public or a discoverable room
  // (docs/room-content-visibility.md, "Joining a room"). Any other room is
  // reachable only through an invitation: once left, its page is closed to the
  // reader.
  const canFollowAgain = room.isPublic || room.discoverable;
  const unfollowing = useConfirmation<null>();

  async function refreshMembership(): Promise<void> {
    await revalidate("/webapp/private/rooms");
    void revalidate("/webapp/private/playlists/recommended");
    await revalidate("/webapp/private/rooms/:id", { param: { id: room.id } });
    await revalidate("/webapp/private/rooms/:id/join/check", {
      param: { id: room.id },
    });
    await reloadCurrentUser();
  }

  async function unfollow(): Promise<"done" | "failed"> {
    try {
      await apiClient.delete("/webapp/private/rooms/:id/leave", {
        param: { id: room.id },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return "failed";
    }
    await refreshMembership();
    const message = t("You unfollowed {{room}}.", { room: roomName });
    if (!canFollowAgain) {
      history.replace("/rooms");
      displayToast(message, "info");
      return "done";
    }
    displayToastWithActions(message, "info", [
      {
        label: t("Undo"),
        onClick: () => {
          // A room asks nothing before a join (docs/partner-objection.md,
          // REQ-05), and a leave keeps the objection rows. The reader already
          // went through this room's welcome: Undo puts the membership back
          // without greeting them again.
          void rejoin();
        },
      },
    ]);
    return "done";
  }

  return (
    <div className="flex items-center gap-3 pt-2">
      {/* The DS Button (Figma "Ready for dev"): Follow is the primary,
          Share and the menu the outline. */}
      {!isMember && canShowJoin ? (
        <Button
          type="button"
          size="sm"
          disabled={isJoining}
          leftIcon={<IconPlus stroke={1.5} />}
          onClick={() => {
            void joinRoom();
          }}
        >
          {t("Follow")}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        leftIcon={<ShareV2Icon />}
        onClick={() => {
          void triggerShare();
        }}
      >
        {t("Share")}
      </Button>
      <RoomLanguageMenu room={room} />
      <Button
        ref={menu.anchor.triggerRef}
        type="button"
        variant="outline"
        size="sm"
        aria-label={t("More")}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        onClick={menu.toggle}
        className="w-9 px-0"
      >
        <IconDots stroke={1.5} />
      </Button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className="w-72">
          <DesktopMenuItem
            description={null}
            icon={IconLink}
            label={t("Copy link")}
            trailing={null}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                void copyLink();
              },
            }}
          />
          {isMember ? (
            <DesktopMenuItem
              description={
                isPinned
                  ? t("JuisciGPT will open by default again")
                  : replacedRoomName === null
                    ? t(
                        "This room will be the default one you see when you open the app"
                      )
                    : t("Replaces {{room}}", { room: replacedRoomName })
              }
              icon={isPinned ? IconPinnedOff : IconPin}
              label={isPinned ? t("Unpin room") : t("Pin to top")}
              trailing={null}
              tone="default"
              action={{
                kind: "button",
                onClick: () => {
                  menu.close();
                  const target = { id: room.id, name: roomName };
                  void (isPinned ? unpin(target) : pin(target));
                },
              }}
            />
          ) : null}
          {isMember ? (
            <DesktopMenuItem
              description={null}
              icon={IconUserMinus}
              label={t("Unfollow room")}
              trailing={null}
              tone="default"
              action={{
                kind: "button",
                onClick: () => {
                  menu.close();
                  unfollowing.ask(null);
                },
              }}
            />
          ) : null}
        </DesktopMenu>
      ) : null}
      {renderShareSheet()}
      {unfollowing.state.status === "closed" ? null : (
        <DesktopConfirmDialog
          title={t("Unfollow this room?")}
          confirmLabel={t("Unfollow")}
          note={
            canFollowAgain
              ? t("You can follow it again at any time.")
              : t(
                  "This room is private: you will need a new invitation to follow it again."
                )
          }
          disabled={unfollowing.state.status === "running"}
          confirmDisabled={false}
          onConfirm={() => {
            void unfollowing.confirm(unfollow);
          }}
          onCancel={unfollowing.cancel}
        >
          <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
            {t("Its new articles will no longer reach your feed:")}
          </p>
          <p className="m-0 font-dm-sans text-sm font-semibold text-ds-slate-950">
            {roomName}
          </p>
        </DesktopConfirmDialog>
      )}
    </div>
  );
}

/**
 * The language of the room's content (Figma "D3.explore.room.language"), for a
 * room published in several: the rows of the account's Language submenu, kept
 * to the languages of this room. A click applies at once, and the page changing
 * is the confirmation. The room's name, description, articles and collections
 * follow; the interface stays on the reader's language
 * (docs/language-system.md).
 */
export function RoomLanguageMenu({
  room,
}: {
  room: Pick<PrivateRoom, "id" | "allowedLanguages">;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { activeLang, contentLang } = useActiveLang();
  const menu = usePopoverMenu("left");
  const languages = LANGUAGE_ORDER.filter((lang) =>
    room.allowedLanguages.includes(lang)
  );
  if (languages.length < 2) return null;

  // In the sentence's own casing ("français"), which the picker's endonyms
  // ("Français") do not have.
  const appLanguage = new Intl.DisplayNames([activeLang], {
    type: "language",
  }).of(activeLang);
  if (appLanguage === undefined)
    throw new Error(`should not happen: no name for ${activeLang}`);

  async function choose(language: SupportedLanguage): Promise<void> {
    const previous = roomContentLangStore.get();
    const selection = { language, source: "explicit" } as const;
    setRoomContentLang(selection);
    navClickChangeLanguage(language, {
      initial_language: contentLang,
      origin: "room",
    });
    try {
      await apiClient.patch("/webapp/private/users/room/:roomId/settings", {
        param: { roomId: room.id },
        json: { language },
      });
    } catch {
      // Only while this choice still holds: a later pick, or the next room,
      // has moved the store on and must not be overwritten.
      if (roomContentLangStore.get() === selection)
        setRoomContentLang(previous);
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
  }

  const currentLabel = LANGUAGE_LABELS[contentLang].native;
  return (
    <>
      <Button
        ref={menu.anchor.triggerRef}
        type="button"
        variant="outline"
        size="sm"
        aria-label={t("Room language: {{language}}", {
          language: currentLabel,
        })}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        leftIcon={<IconWorld stroke={1.5} />}
        rightIcon={<IconChevronDown stroke={1.5} />}
        onClick={menu.toggle}
      >
        {currentLabel}
      </Button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className="w-66">
          {languages.map((lang) => (
            <DesktopMenuRadioItem
              key={lang}
              label={LANGUAGE_LABELS[lang].name}
              hint={LANGUAGE_LABELS[lang].native}
              checked={lang === contentLang}
              onClick={() => {
                menu.close();
                if (lang === contentLang) return;
                void choose(lang);
              }}
            />
          ))}
          <DesktopMenuDivider />
          <p className="m-0 px-2.5 py-1.5 text-desktop-meta text-ds-slate-700">
            {t(
              "For this room only. The rest of the app stays in {{language}}.",
              {
                language: appLanguage,
              }
            )}
          </p>
        </DesktopMenu>
      ) : null}
    </>
  );
}
